import { Flex, Text } from "@chakra-ui/react";
import { GroupBase, OptionProps, Props, Select } from "chakra-react-select";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import {
  PropertyForTheCompanyModel,
  usePropertiesForTheCompany,
} from "hooks/usePropertiesForTheCompany";

const CustomOption = ({ data, innerProps }: OptionProps<PropertyForTheCompanyModel>) => (
  <Flex
    {...innerProps}
    p={2}
    gap={4}
    borderRadius="md"
    _hover={{ bg: "gray.50" }}
    cursor="pointer"
    alignItems="center"
  >
    <Flex direction="column" alignItems="start">
      <Text color="gray.600">{data.name}</Text>
      <Text fontSize="sm" color="gray.500">
        {data.address}
      </Text>
    </Flex>
  </Flex>
);

interface PropertySelectProps
  extends Props<PropertyForTheCompanyModel, false, GroupBase<PropertyForTheCompanyModel>> {
  property: PropertyForTheCompanyModel | null;
  setProperty: (property: PropertyForTheCompanyModel | null) => void;
  companyId?: string | null;
  entityId?: string | null;
}

export const PropertySelect = ({
  property,
  setProperty,
  companyId,
  entityId,
  ...props
}: PropertySelectProps) => {
  const { user } = useTenant();
  const { data: properties, isLoading } = usePropertiesForTheCompany(
    companyId ?? user.lender_company.id,
    entityId
  );

  return (
    <Select<PropertyForTheCompanyModel>
      options={properties?.sort((a, b) => a.name.localeCompare(b.name)) ?? []}
      getOptionLabel={p => p.name}
      getOptionValue={p => p.id.toString()}
      components={{ Option: CustomOption }}
      isLoading={isLoading}
      chakraStyles={{
        placeholder: provided => ({
          ...provided,
          textAlign: "left",
        }),
        singleValue: provided => ({
          ...provided,
          textAlign: "left",
        }),
      }}
      placeholder="Select a property..."
      noOptionsMessage={() => "No properties found."}
      onChange={newValue => setProperty(newValue)}
      value={property}
      variant="primary"
      filterOption={(option, inputValue) =>
        [option.data.name, option.data.address].some(field =>
          field?.toLowerCase().includes(inputValue.toLowerCase())
        )
      }
      isClearable={true}
      {...props}
    />
  );
};
